import React, { useEffect, useState, useMemo } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { GraduationCap, BookOpen, ChevronRight, Search, Trash2 } from "lucide-react";

const STATUSES = ["pending", "deposit_paid", "paid_in_full", "plan_active", "overdue"];

const PRIMAIRE_ORDER = ["GS", "CP", "CE1", "CE2", "CM1", "CM2"];
const SECONDAIRE_ORDER = ["6e", "5e", "4e", "3e", "2nde", "1ère", "Terminale"];

const normalizeGrade = (g) => {
  if (!g) return "—";
  const k = g.trim();
  return k || "—";
};

export default function AdminPayments() {
  const { t, lang } = useLang();
  const { isSuperAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandPrim, setExpandPrim] = useState(true);
  const [expandSec, setExpandSec] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/payments")
      .then(r => setItems(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const purgeUnclassified = async () => {
    if (!window.confirm(lang === "fr"
      ? "Supprimer définitivement tous les plans 'Non classé' ? Cette action est irréversible."
      : "Permanently delete all 'Unclassified' plans? This cannot be undone.")) return;
    setPurging(true);
    try {
      const r = await api.delete("/admin/payments/purge-unclassified");
      toast.success(lang === "fr"
        ? `${r.data.deleted} plans supprimés`
        : `${r.data.deleted} plans deleted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPurging(false);
    }
  };

  const update = async (id, status) => {
    try { await api.patch(`/payments/${id}`, { status }); toast.success(lang === "fr" ? "Mis à jour" : "Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

  const grouped = useMemo(() => {
    const filtered = items.filter(p => {
      const q = filter.trim().toLowerCase();
      const matchQ = !q || (
        (p.family?.parent_first_name + " " + p.family?.parent_last_name + " " + p.family?.email + " " + p.student?.first_name + " " + p.student?.last_name).toLowerCase().includes(q)
      );
      const matchS = statusFilter === "all" || p.status === statusFilter;
      return matchQ && matchS;
    });

    const byLevel = { primaire: {}, secondaire: {}, unknown: {} };
    for (const p of filtered) {
      const lvl = p.level || "unknown";
      const g = normalizeGrade(p.grade);
      if (!byLevel[lvl][g]) byLevel[lvl][g] = [];
      byLevel[lvl][g].push(p);
    }
    return byLevel;
  }, [items, filter, statusFilter]);

  const totals = useMemo(() => {
    const sumGroup = (group) => {
      let count = 0, annual = 0, paid = 0;
      for (const arr of Object.values(group)) {
        for (const p of arr) {
          count += 1;
          annual += p.amount || 0;
          if (p.status === "paid_in_full" || p.status === "deposit_paid" || p.status === "plan_active") {
            paid += p.amount || 0;
          }
        }
      }
      return { count, annual, paid };
    };
    return {
      primaire: sumGroup(grouped.primaire),
      secondaire: sumGroup(grouped.secondaire),
      unknown: sumGroup(grouped.unknown),
    };
  }, [grouped]);

  const sortGradeKeys = (level, keys) => {
    const order = level === "primaire" ? PRIMAIRE_ORDER : SECONDAIRE_ORDER;
    return [...keys].sort((a, b) => {
      const ia = order.findIndex(o => o.toLowerCase() === a.toLowerCase());
      const ib = order.findIndex(o => o.toLowerCase() === b.toLowerCase());
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };

  return (
    <div data-testid="admin-payments">
      <p className="overline mb-1">OFA France · Admin</p>
      <h1 className="font-serif text-3xl tracking-tight mb-2">{t("admin.payments")}</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        {lang === "fr"
          ? "Vue regroupée des plans de paiement par niveau puis par classe. Tarif unique : 1 995 €/cours/an (GS → Terminale)."
          : "Payment plans grouped by level then by grade. Flat tuition: €1,995/course/year (GS → Terminale)."}
      </p>

      {/* Filters */}
      <Card className="mb-5">
        <CardContent className="p-4 grid md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="payments-search"
              placeholder={lang === "fr" ? "Rechercher (nom, email, élève)…" : "Search (name, email, student)…"}
              className="pl-9"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === "fr" ? "Tous statuts" : "All statuses"}</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{t(`statuses.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground self-center">
            {totals.primaire.count + totals.secondaire.count + totals.unknown.count} {lang === "fr" ? "plans" : "plans"}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-8 text-muted-foreground">{lang === "fr" ? "Chargement…" : "Loading…"}</div>}

      {!loading && (
        <>
          {/* Primaire */}
          <LevelSection
            data-testid="level-primaire"
            level="primaire"
            icon={BookOpen}
            color="hsl(200 70% 50%)"
            title={lang === "fr" ? "Primaire" : "Primary"}
            subtitle={lang === "fr" ? "GS → CM2 · 1 995 €/cours/an" : "GS → CM2 · €1,995/course/yr"}
            count={totals.primaire.count}
            annual={totals.primaire.annual}
            paid={totals.primaire.paid}
            expanded={expandPrim}
            onToggle={() => setExpandPrim(!expandPrim)}
            grades={grouped.primaire}
            sortedKeys={sortGradeKeys("primaire", Object.keys(grouped.primaire))}
            fmt={fmt} t={t} lang={lang} update={update}
          />

          {/* Secondaire */}
          <LevelSection
            data-testid="level-secondaire"
            level="secondaire"
            icon={GraduationCap}
            color="hsl(var(--accent))"
            title={lang === "fr" ? "Secondaire" : "Secondary"}
            subtitle={lang === "fr" ? "6e → Terminale · 1 995 €/cours/an" : "6e → Terminale · €1,995/course/yr"}
            count={totals.secondaire.count}
            annual={totals.secondaire.annual}
            paid={totals.secondaire.paid}
            expanded={expandSec}
            onToggle={() => setExpandSec(!expandSec)}
            grades={grouped.secondaire}
            sortedKeys={sortGradeKeys("secondaire", Object.keys(grouped.secondaire))}
            fmt={fmt} t={t} lang={lang} update={update}
          />

          {/* Unknown (no grade) */}
          {totals.unknown.count > 0 && (
            <>
              {isSuperAdmin && (
                <div className="flex justify-end mb-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={purgeUnclassified}
                    disabled={purging}
                    data-testid="purge-unclassified-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    {purging
                      ? (lang === "fr" ? "Suppression…" : "Deleting…")
                      : (lang === "fr" ? `Purger ${totals.unknown.count} plans non classés` : `Purge ${totals.unknown.count} unclassified plans`)}
                  </Button>
                </div>
              )}
              <LevelSection
              data-testid="level-unknown"
              level="unknown"
              title={lang === "fr" ? "Non classé" : "Unclassified"}
              subtitle={lang === "fr" ? "Élèves sans niveau renseigné" : "Students without grade"}
              count={totals.unknown.count}
              annual={totals.unknown.annual}
              paid={totals.unknown.paid}
              expanded={true}
              onToggle={() => {}}
              grades={grouped.unknown}
              sortedKeys={Object.keys(grouped.unknown)}
              fmt={fmt} t={t} lang={lang} update={update}
            />
            </>
          )}

          {totals.primaire.count + totals.secondaire.count + totals.unknown.count === 0 && (
            <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
              {lang === "fr" ? "Aucun plan de paiement à afficher." : "No payment plans to display."}
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}

function LevelSection({ level, icon: Icon, color, title, subtitle, count, annual, paid, expanded, onToggle, grades, sortedKeys, fmt, t, lang, update, "data-testid": testid }) {
  if (count === 0 && level !== "unknown") {
    return (
      <Card className="mb-4 opacity-60" data-testid={testid}>
        <CardContent className="p-4 flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5" style={{ color }} />}
          <div className="flex-1">
            <div className="font-display font-semibold text-lg">{title}</div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
          <div className="text-xs text-muted-foreground">{lang === "fr" ? "Aucun plan" : "No plans"}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-5" data-testid={testid}>
      <CardContent className="p-0">
        <button
          onClick={onToggle}
          className="w-full p-5 flex items-center gap-4 hover:bg-secondary/30 transition-colors text-left"
          data-testid={`${testid}-toggle`}
        >
          {Icon && <Icon className="w-6 h-6 shrink-0" style={{ color }} />}
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-xl tracking-tight">{title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
          </div>
          <div className="hidden sm:flex gap-5 text-right shrink-0">
            <Stat label={lang === "fr" ? "Plans" : "Plans"} value={count} />
            <Stat label={lang === "fr" ? "Annuel total" : "Annual total"} value={`${fmt(annual)} €`} />
            <Stat label={lang === "fr" ? "Encaissé" : "Collected"} value={`${fmt(paid)} €`} color={color} />
          </div>
          <ChevronRight className={`w-5 h-5 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </button>

        {expanded && (
          <div className="border-t border-border">
            {sortedKeys.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">
                {lang === "fr" ? "Aucun plan dans ce niveau." : "No plans at this level."}
              </div>
            ) : sortedKeys.map(grade => (
              <GradeBlock
                key={grade}
                grade={grade}
                payments={grades[grade]}
                fmt={fmt} t={t} lang={lang} update={update}
                color={color}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GradeBlock({ grade, payments, fmt, t, lang, update, color }) {
  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
  return (
    <div className="border-b border-border last:border-b-0" data-testid={`grade-block-${grade}`}>
      <div className="px-5 py-3 bg-secondary/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-display font-semibold text-sm tabular-nums px-2.5 py-0.5 rounded-full" style={{ background: `${color}1a`, color }}>
            {grade}
          </span>
          <span className="text-xs text-muted-foreground">
            {payments.length} {payments.length > 1 ? (lang === "fr" ? "plans" : "plans") : "plan"}
          </span>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {fmt(total)} €
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {payments.map(p => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-t border-border/40 hover:bg-secondary/20">
                <td className="px-5 py-3">
                  <div className="font-medium">
                    {p.student ? `${p.student.first_name} ${p.student.last_name}` : "—"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {p.family?.parent_first_name} {p.family?.parent_last_name} · {p.family?.email}
                  </div>
                </td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{t(`payments.${p.mode}`)}</td>
                <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap">{fmt(p.amount)} €</td>
                <td className="px-3 py-3 whitespace-nowrap"><Badge>{t(`statuses.${p.status}`)}</Badge></td>
                <td className="px-5 py-3">
                  <Select value={p.status} onValueChange={(v) => update(p.id, v)}>
                    <SelectTrigger className="w-40 h-8" data-testid={`payment-update-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{t(`statuses.${s}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display font-semibold text-sm tabular-nums" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}
